<script setup>
import { VDataTable } from 'vuetify/labs/VDataTable'
import { useSnackBar } from '@/utils/stores/snackbar'
import { getProjectId } from '@/utils/cdms'

definePage({
  meta: {
    action: 'open',
    subject: 'rtms',
  },
})

const snackbarStore = useSnackBar()
const projectId = getProjectId()

// 소수점 포맷팅
const decimalType = ref('integer')
const formatQty = (val) => {
  if (val == null || val === '') return '-'
  const num = Number(val)
  if (isNaN(num)) return val
  if (decimalType.value === 'decimal1') return num.toFixed(1)
  if (decimalType.value === 'decimal2') return num.toFixed(2)
  return String(Math.floor(num))
}

// decimalType에 따라 소수점 입력 제한
const onDecimalInput = (e) => {
  const maxDecimals = decimalType.value === 'integer' ? 0 : decimalType.value === 'decimal1' ? 1 : 2
  const allowed = maxDecimals > 0 ? /[0-9.]/ : /[0-9]/
  if (!allowed.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
    return
  }
  const target = e.target
  const val = target.value
  if (maxDecimals === 0 && e.key === '.') {
    e.preventDefault()
    return
  }
  if (e.key === '.' && val.includes('.')) {
    e.preventDefault()
    return
  }
  const dotIdx = val.indexOf('.')
  if (dotIdx !== -1 && target.selectionStart > dotIdx && val.length - dotIdx > maxDecimals && !['Backspace', 'Delete'].includes(e.key)) {
    e.preventDefault()
  }
}

// ─────────────────────────────────────────
// 관리번호 테이블
// ─────────────────────────────────────────
const managementNumbers = ref([])
const mnHeaders = [
  { title: '관리번호', key: 'management_number', width: '80px', align: 'center' },
  { title: '군유형(군배정)', key: 'group_type', width: '110px' },
  { title: '명칭', key: 'drug_name', width: '140px' },
  { title: 'Lot No.', key: 'lot_no', width: '120px' },
  { title: '사용기한', key: 'expiry_date', width: '110px' },
  { title: '총 입수량', key: 'total_stock_in', width: '90px', align: 'center' },
  { title: '총 배포량', key: 'total_delivered', width: '90px', align: 'center' },
  { title: '현재 재고량', key: 'current_stock', width: '100px', align: 'center' },
  { title: '한계재고량', key: 'threshold', width: '100px', align: 'center' },
  { title: '1마리당 사용약물량', key: 'per_animal_usage', width: '140px', align: 'center' },
  { title: '관리', key: 'actions', width: '280px', align: 'center', sortable: false },
]

const loadManagementNumbers = async () => {
  const { data, error } = await useApi(createUrl('/drug-management/management-numbers', {
    query: { projectId },
  }))

  if (!error.value && data.value) {
    managementNumbers.value = data.value.items ?? data.value
    if (data.value.decimalType) decimalType.value = data.value.decimalType
  }
}

// ─────────────────────────────────────────
// 관리번호 등록/수정 다이얼로그
// ─────────────────────────────────────────
const isMnDialogOpen = ref(false)
const isMnEditMode = ref(false)
const mnEditId = ref(null)
const mnForm = reactive({
  management_number: 1,
  group_type: '',
  drug_name: '',
  lot_no: '',
  expiry_date: '',
  threshold: null,
  threshold_na: false,
  per_animal_usage: null,
  per_animal_usage_na: false,
})
const refMnForm = ref(null)

// 군 유형 옵션
const groupTypeOptions = ref([])

const loadGroupTypes = async () => {
  try {
    const { data, error } = await useApi(createUrl('/group-assignment', {
      query: { projectId },
    }))

    if (!error.value && data.value) {
      const list = Array.isArray(data.value?.groups) ? data.value.groups : []

      groupTypeOptions.value = list.map(g => ({
        value: g.name,
        title: g.name,
      }))
    }
  } catch (e) {
    groupTypeOptions.value = []
  }
}

const openMnCreateDialog = async () => {
  if (!decimalType.value) {
    snackbarStore.showSnackBar('약물처방 질문형이 설정되지 않았습니다. 문서개발에서 약물처방 질문을 먼저 생성해주세요.', 'error')
    return
  }
  isMnEditMode.value = false
  mnEditId.value = null

  const { data } = await useApi(createUrl('/drug-management/management-numbers/next', {
    query: { projectId },
  }))

  Object.assign(mnForm, {
    management_number: data.value?.next ?? 1,
    group_type: '',
    drug_name: '',
    lot_no: '',
    expiry_date: '',
    threshold: null,
    threshold_na: false,
    per_animal_usage: null,
    per_animal_usage_na: false,
  })
  isMnDialogOpen.value = true
}

const openMnEditDialog = (item) => {
  isMnEditMode.value = true
  mnEditId.value = item.id
  Object.assign(mnForm, {
    management_number: item.management_number,
    group_type: item.group_type,
    drug_name: item.drug_name,
    lot_no: item.lot_no ?? '',
    expiry_date: item.expiry_date ?? '',
    threshold: item.threshold,
    threshold_na: item.threshold === null,
    per_animal_usage: item.per_animal_usage,
    per_animal_usage_na: item.per_animal_usage === null,
  })
  isMnDialogOpen.value = true
}

const saveMnForm = async () => {
  const validate = await refMnForm.value?.validate()
  if (!validate?.valid) return

  const body = {
    model: {
      project_id: projectId,
      management_number: mnForm.management_number,
      group_type: mnForm.group_type,
      drug_name: mnForm.drug_name,
      lot_no: mnForm.lot_no || null,
      expiry_date: mnForm.expiry_date || null,
      threshold: mnForm.threshold_na ? null : mnForm.threshold,
      per_animal_usage: mnForm.per_animal_usage_na ? null : mnForm.per_animal_usage,
    },
  }

  try {
    if (isMnEditMode.value) {
      const res = await $api(`/drug-management/management-numbers/${mnEditId.value}`, { method: 'PATCH', body })
      if (res?.code === 'ERROR') {
        snackbarStore.showSnackBar(res.message || '수정에 실패했습니다.', 'error')
        return
      }
      snackbarStore.showSnackBar('관리번호가 수정되었습니다.')
    } else {
      const res = await $api('/drug-management/management-numbers', { method: 'POST', body })
      if (res?.code === 'ERROR') {
        snackbarStore.showSnackBar(res.message || '등록에 실패했습니다.', 'error')
        return
      }
      snackbarStore.showSnackBar('관리번호가 등록되었습니다.')
    }

    isMnDialogOpen.value = false
    await loadManagementNumbers()
  } catch (e) {
    const msg = e?.data?.message || '처리에 실패했습니다.'
    snackbarStore.showSnackBar(msg, 'error')
  }
}

const deleteMn = async (item) => {
  if (!confirm(`관리번호 ${item.management_number}을(를) 삭제하시겠습니까?`)) return

  await $api(`/drug-management/management-numbers/${item.id}`, { method: 'DELETE' })
  snackbarStore.showSnackBar('관리번호가 삭제되었습니다.')
  await loadManagementNumbers()
}

// ─────────────────────────────────────────
// 입고 등록 다이얼로그
// ─────────────────────────────────────────
const isStockInDialogOpen = ref(false)
const stockInForm = reactive({
  management_number_id: null,
  management_label: '',
  stock_in_date: new Date().toISOString().slice(0, 10),
  quantity: '',
  receiver: '',
})
const refStockInForm = ref(null)

const openStockInDialog = (item) => {
  stockInForm.management_number_id = item.id
  stockInForm.management_label = `${item.management_number} - ${item.drug_name}`
  stockInForm.stock_in_date = new Date().toISOString().slice(0, 10)
  stockInForm.quantity = ''
  stockInForm.receiver = ''
  isStockInDialogOpen.value = true
}

const saveStockIn = async () => {
  const validate = await refStockInForm.value?.validate()
  if (!validate?.valid) return

  await $api('/drug-management/stock-ins', {
    method: 'POST',
    body: {
      model: {
        project_id: projectId,
        management_number_id: stockInForm.management_number_id,
        stock_in_date: stockInForm.stock_in_date,
        quantity: Number(stockInForm.quantity),
        receiver: stockInForm.receiver,
      },
    },
  })

  snackbarStore.showSnackBar('입고가 등록되었습니다.')
  isStockInDialogOpen.value = false
  await loadManagementNumbers()
}

// ─────────────────────────────────────────
// 입고 이력 다이얼로그
// ─────────────────────────────────────────
const isStockInHistoryOpen = ref(false)
const stockInHistoryLabel = ref('')
const stockInHistoryItems = ref([])
const stockInHistoryHeaders = [
  { title: '입고일', key: 'stock_in_date', width: '140px' },
  { title: '입고수량', key: 'quantity', width: '120px', align: 'center' },
  { title: '수령자', key: 'receiver', width: '140px' },
]

const openStockInHistory = async (item) => {
  stockInHistoryLabel.value = `${item.management_number} - ${item.drug_name}`

  const { data, error } = await useApi(createUrl('/drug-management/stock-ins', {
    query: { managementNumberId: item.id },
  }))

  stockInHistoryItems.value = (!error.value && data.value) ? data.value : []
  isStockInHistoryOpen.value = true
}

// ─────────────────────────────────────────
// Site 목록 (필터용)
// ─────────────────────────────────────────
const siteOptions = ref([])

const loadSites = async () => {
  try {
    const { data, error } = await useApi(createUrl('/sources/sites/all', {
      query: { projectId },
    }))

    if (!error.value && data.value) {
      siteOptions.value = data.value.map(s => ({
        value: s.site_id ?? s.id,
        title: s.name ?? `SITE-${s.site_id ?? s.id}`,
      })).sort((a, b) => a.title.localeCompare(b.title, 'ko'))
    }
  } catch (e) {
    siteOptions.value = []
  }
}

// ─────────────────────────────────────────
// DRUG No. 목록
// ─────────────────────────────────────────
const drugs = ref([])
const drugFilterMn = ref('')
const drugFilterSite = ref('')
const drugFilterSearch = ref('')
const drugPage = ref(1)
const drugItemsPerPage = ref(15)

const drugHeaders = [
  { title: 'DRUG No.', key: 'drug_no', width: '120px' },
  { title: '관리번호', key: 'mn_number', width: '80px', align: 'center' },
  { title: '군유형(군배정)', key: 'mn_group_type', width: '110px' },
  { title: '명칭', key: 'mn_drug_name', width: '140px' },
  { title: 'Lot No.', key: 'mn_lot_no', width: '110px' },
  { title: '사용기한', key: 'mn_expiry_date', width: '110px' },
  { title: '수량', key: 'quantity', width: '80px', align: 'center' },
  { title: '위치', key: 'location', width: '100px', align: 'center' },
  { title: 'SITE No.', key: 'site_no', width: '100px', align: 'center' },
  { title: '등록일', key: 'created_at', width: '110px' },
  { title: '관리', key: 'drug_actions', width: '100px', align: 'center', sortable: false },
]

const loadDrugs = async () => {
  const query = { projectId }

  if (drugFilterMn.value) query.managementNumberId = drugFilterMn.value
  if (drugFilterSite.value) query.siteId = drugFilterSite.value
  if (drugFilterSearch.value) query.search = drugFilterSearch.value

  const { data, error } = await useApi(createUrl('/drug-management/drugs', { query }))

  if (!error.value && data.value) {
    if (data.value.decimalType) decimalType.value = data.value.decimalType
    const items = data.value.items ?? data.value
    // 중첩 키 대신 flat하게 변환
    drugs.value = items.map(d => ({
      ...d,
      mn_number: d.management_number?.management_number ?? '-',
      mn_group_type: d.management_number?.group_type ?? '-',
      mn_drug_name: d.management_number?.drug_name ?? '-',
      mn_lot_no: d.management_number?.lot_no ?? '-',
      mn_expiry_date: formatDate(d.management_number?.expiry_date),
    }))
  }
}

const resetDrugFilters = () => {
  drugFilterMn.value = ''
  drugFilterSite.value = ''
  drugFilterSearch.value = ''
  loadDrugs()
}

// 관리번호 드롭다운 옵션 (필터용)
const mnFilterOptions = computed(() =>
  managementNumbers.value.map(m => ({
    value: m.id,
    title: `${m.management_number} - ${m.drug_name}`,
  })),
)

// ─────────────────────────────────────────
// DRUG No. 등록 다이얼로그
// ─────────────────────────────────────────
const isDrugDialogOpen = ref(false)
const drugRegForm = reactive({
  management_number_id: null,
  items: [{ drug_no: '', quantity: 1 }],
})

// 선택한 관리번호의 상세 정보
const selectedMnDetail = computed(() => {
  if (!drugRegForm.management_number_id) return null
  return managementNumbers.value.find(m => m.id === drugRegForm.management_number_id)
})

const openDrugDialog = () => {
  drugRegForm.management_number_id = null
  drugRegForm.items = [{ drug_no: '', quantity: 1 }]
  isDrugDialogOpen.value = true
}

const addDrugRow = () => {
  drugRegForm.items.push({ drug_no: '', quantity: 1 })
}

const removeDrugRow = (idx) => {
  if (drugRegForm.items.length > 1) {
    drugRegForm.items.splice(idx, 1)
  }
}

const saveDrugs = async () => {
  if (!drugRegForm.management_number_id) {
    snackbarStore.showSnackBar('관리번호를 선택하세요.', 'error')
    return
  }

  // 입고 등록이 된 관리번호만 Drug No. 등록 가능
  const selectedMn = managementNumbers.value.find(m => m.id === drugRegForm.management_number_id)
  if (selectedMn && (selectedMn.total_stock_in ?? 0) <= 0) {
    snackbarStore.showSnackBar('입고 등록이 된 경우에만 Drug No. 등록이 가능합니다.', 'error')
    return
  }

  const validItems = drugRegForm.items.filter(i => i.drug_no.trim())
  if (validItems.length === 0) {
    snackbarStore.showSnackBar('DRUG No.를 1개 이상 입력하세요.', 'error')
    return
  }

  const items = validItems.map(i => ({
    management_number_id: drugRegForm.management_number_id,
    drug_no: i.drug_no.trim(),
    quantity: i.quantity || 1,
  }))

  // 수량 초과 검증
  const totalNewQty = items.reduce((sum, i) => sum + Number(i.quantity), 0)
  const existingDrugQty = drugs.value
    .filter(d => d.management_number?.id === drugRegForm.management_number_id)
    .reduce((sum, d) => sum + Number(d.quantity || 0), 0)
  const available = (selectedMn?.total_stock_in ?? 0) - existingDrugQty
  if (totalNewQty > available) {
    snackbarStore.showSnackBar(`등록 가능 수량을 초과합니다. (가용: ${formatQty(available)}, 요청: ${formatQty(totalNewQty)})`, 'error')
    return
  }

  try {
    const res = await $api('/drug-management/drugs', {
      method: 'POST',
      body: { projectId, items },
    })

    if (res?.code === 'ERROR') {
      snackbarStore.showSnackBar(res.message || '등록에 실패했습니다.', 'error')
      return
    }

    snackbarStore.showSnackBar(`DRUG No. ${items.length}건이 등록되었습니다.`)
    isDrugDialogOpen.value = false
    await loadDrugs()
    await loadManagementNumbers()
  } catch (e) {
    const msg = e?.data?.message || '등록에 실패했습니다.'
    snackbarStore.showSnackBar(msg, 'error')
  }
}

// ─────────────────────────────────────────
// DRUG No. 수정 다이얼로그
// ─────────────────────────────────────────
const isDrugEditDialogOpen = ref(false)
const drugEditId = ref(null)
const drugEditForm = reactive({
  drug_no: '',
  management_number_id: null,
  quantity: 1,
})

const openDrugEditDialog = (item) => {
  drugEditId.value = item.id
  drugEditForm.drug_no = item.drug_no
  drugEditForm.management_number_id = item.management_number_id
  drugEditForm.quantity = item.quantity
  isDrugEditDialogOpen.value = true
}

const saveDrugEdit = async () => {
  if (!drugEditForm.drug_no.trim()) {
    snackbarStore.showSnackBar('DRUG No.를 입력하세요.', 'error')
    return
  }

  // 수량 초과 검증
  const mn = managementNumbers.value.find(m => m.id === drugEditForm.management_number_id)
  if (mn) {
    const otherDrugQty = drugs.value
      .filter(d => d.management_number?.id === drugEditForm.management_number_id && d.id !== drugEditId.value)
      .reduce((sum, d) => sum + Number(d.quantity || 0), 0)
    const available = (mn.total_stock_in ?? 0) - otherDrugQty
    if (Number(drugEditForm.quantity) > available) {
      snackbarStore.showSnackBar(`등록 가능 수량을 초과합니다. (가용: ${formatQty(available)}, 요청: ${formatQty(drugEditForm.quantity)})`, 'error')
      return
    }
  }

  try {
    const res = await $api(`/drug-management/drugs/${drugEditId.value}`, {
      method: 'PATCH',
      body: {
        model: {
          drug_no: drugEditForm.drug_no.trim(),
          management_number_id: drugEditForm.management_number_id,
          quantity: drugEditForm.quantity,
        },
      },
    })

    if (res?.code === 'ERROR') {
      snackbarStore.showSnackBar(res.message || '수정에 실패했습니다.', 'error')
      return
    }

    snackbarStore.showSnackBar('DRUG No.가 수정되었습니다.')
    isDrugEditDialogOpen.value = false
    await loadDrugs()
  } catch (e) {
    const msg = e?.data?.message || '수정에 실패했습니다.'
    snackbarStore.showSnackBar(msg, 'error')
  }
}

// DRUG No. 삭제
const deleteDrug = async (item) => {
  if (!confirm(`${item.drug_no}을(를) 삭제하시겠습니까?`)) return

  await $api(`/drug-management/drugs/${item.id}`, { method: 'DELETE' })
  snackbarStore.showSnackBar('삭제되었습니다.')
  await loadDrugs()
  await loadManagementNumbers()
}

// ─────────────────────────────────────────
// 엑셀 다운로드 (이메일 발송)
// ─────────────────────────────────────────
const downloadMnExcel = async () => {
  try {
    await $api('/drug-management/export', {
      method: 'POST',
      body: { projectId, exportType: 'management_numbers' },
    })
    snackbarStore.showSnackBar('요청이 완료되었습니다. 결과는 이메일로 전달됩니다.', 'success')
  } catch (e) {
    snackbarStore.showSnackBar('엑셀 다운로드 요청에 실패했습니다.', 'error')
  }
}

// 날짜 포맷
const formatDate = (val) => {
  if (!val) return '-'
  return String(val).slice(0, 10)
}

// 다이얼로그 닫기: persistent 유지 + 수동 ESC/바깥클릭 처리
let clickStartedOnFlatpickr = false

const onDocPointerDown = (e) => {
  clickStartedOnFlatpickr = !!e.target.closest('.flatpickr-calendar')
}

const onDocKeyDown = (e) => {
  if (e.key !== 'Escape') return
  if (document.querySelector('.flatpickr-calendar.open')) return
  if (isStockInDialogOpen.value) { isStockInDialogOpen.value = false; return }
  if (isMnDialogOpen.value) { isMnDialogOpen.value = false; return }
}

const onMnOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isMnDialogOpen.value = false
}

const onStockInOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isStockInDialogOpen.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true)
  document.addEventListener('keydown', onDocKeyDown, true)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  document.removeEventListener('keydown', onDocKeyDown, true)
})

// 키 이벤트 핸들러 (template에서 직접 modifier 사용 시 _withKeys 에러 발생하므로 함수로 처리)
const onSearchKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    loadDrugs()
  }
}

const onDrugNoKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    addDrugRow()
  }
}

// 초기 데이터 로드
onMounted(async () => {
  await loadGroupTypes()
  await loadSites()
  await loadManagementNumbers()
  await loadDrugs()
})
</script>

<template>
  <div>
    <!-- ═══════════════════════════════════════ -->
    <!-- 관리번호별 재고 현황                        -->
    <!-- ═══════════════════════════════════════ -->
    <VCard class="mb-6">
      <VCardText>
        <div class="d-flex justify-space-between align-center mb-4">
          <h6 class="text-h6 font-weight-bold">
            <VIcon icon="tabler-clipboard-list" class="me-1" />
            관리번호별 재고 현황
          </h6>
          <div class="d-flex gap-2">
            <VBtn variant="outlined" size="small" prepend-icon="tabler-file-spreadsheet" @click="downloadMnExcel">
              엑셀 다운로드
            </VBtn>
            <VBtn color="primary" size="small" @click="openMnCreateDialog">
              + 관리번호 신규 등록
            </VBtn>
          </div>
        </div>

        <VDataTable
          :items="managementNumbers"
          :headers="mnHeaders"
          density="compact"
          class="custom-table rtms-table"
          :items-per-page="-1"
          hide-default-footer
        >
          <template #item.expiry_date="{ item }">
            {{ formatDate(item.expiry_date) }}
          </template>

          <template #item.total_stock_in="{ item }">
            {{ formatQty(item.total_stock_in) }}
          </template>

          <template #item.total_delivered="{ item }">
            {{ formatQty(item.total_delivered) }}
          </template>

          <template #item.current_stock="{ item }">
            {{ formatQty(item.current_stock) }}
          </template>

          <template #item.threshold="{ item }">
            {{ item.threshold !== null ? formatQty(item.threshold) : 'N/A' }}
          </template>

          <template #item.per_animal_usage="{ item }">
            {{ item.per_animal_usage !== null ? formatQty(item.per_animal_usage) : 'N/A' }}
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <VBtn variant="outlined" color="primary" size="x-small" @click="openMnEditDialog(item)">수정</VBtn>
              <VBtn variant="outlined" color="error" size="x-small" @click="deleteMn(item)">삭제</VBtn>
              <VBtn variant="outlined" size="x-small" @click="openStockInDialog(item)">입고 등록</VBtn>
              <VBtn variant="outlined" size="x-small" @click="openStockInHistory(item)">입고 이력</VBtn>
            </div>
          </template>

          <template #bottom>
            <div class="text-caption text-medium-emphasis pa-2">
              ※ 현재 재고량 = 총 입수량 - 총 배포량
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ═══════════════════════════════════════ -->
    <!-- DRUG No. (재고) 목록                       -->
    <!-- ═══════════════════════════════════════ -->
    <VCard>
      <VCardText>
        <div class="d-flex justify-space-between align-center mb-4">
          <h6 class="text-h6 font-weight-bold">
            <VIcon icon="tabler-package" class="me-1" />
            DRUG No. (재고) 목록
          </h6>
          <VBtn color="primary" size="small" @click="openDrugDialog">
            + DRUG No. 등록
          </VBtn>
        </div>

        <!-- 필터 -->
        <VRow class="mb-3">
          <VCol cols="12" md="3">
            <AppSelect
              v-model="drugFilterMn"
              :items="mnFilterOptions"
              label="관리번호"
              clearable
              placeholder="전체"
            />
          </VCol>
          <VCol cols="12" md="3">
            <AppSelect
              v-model="drugFilterSite"
              :items="siteOptions"
              label="SITE"
              clearable
              placeholder="전체"
            />
          </VCol>
          <VCol cols="12" md="3">
            <AppTextField
              v-model="drugFilterSearch"
              label="DRUG No. 검색"
              placeholder="DRUG No. 입력"
              clearable
              @keydown="onSearchKeydown"
            />
          </VCol>
          <VCol cols="12" md="3" class="d-flex align-end gap-2">
            <VBtn size="small" @click="loadDrugs">검색</VBtn>
            <VBtn variant="outlined" size="small" @click="resetDrugFilters">초기화</VBtn>
          </VCol>
        </VRow>

        <VDataTable
          v-model:items-per-page="drugItemsPerPage"
          v-model:page="drugPage"
          :items="drugs"
          :headers="drugHeaders"
          density="compact"
          class="custom-table rtms-table"
        >
          <template #item.quantity="{ item }">
            {{ formatQty(item.quantity) }}
          </template>

          <template #item.location="{ item }">
            <VChip
              :color="item.location === 'INVENTORY' ? 'primary' : 'success'"
              size="small"
              label
            >
              {{ item.location === 'INVENTORY' ? 'INVENTORY' : 'LOCAL' }}
            </VChip>
          </template>

          <template #item.site_no="{ item }">
            {{ item.site_no ?? '-' }}
          </template>

          <template #item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template #item.drug_actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <VBtn variant="text" color="primary" size="x-small" @click="openDrugEditDialog(item)">
                <VIcon icon="tabler-pencil" size="16" />
              </VBtn>
              <VBtn variant="text" color="error" size="x-small" @click="deleteDrug(item)">
                <VIcon icon="tabler-trash" size="16" />
              </VBtn>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ═══════════════════════════════════════ -->
    <!-- 관리번호 등록/수정 다이얼로그                   -->
    <!-- ═══════════════════════════════════════ -->
    <VDialog v-model="isMnDialogOpen" max-width="520" persistent no-click-animation @click:outside="onMnOutsideClick">
      <VCard :title="isMnEditMode ? '관리번호 수정' : '관리번호 신규 등록'">
        <VCardText>
          <VForm ref="refMnForm" @submit.prevent="saveMnForm">
            <VRow>
              <VCol cols="6">
                <AppTextField
                  v-model="mnForm.management_number"
                  label="관리번호"
                  type="number"
                  :rules="[requiredValidator]"
                  :disabled="isMnEditMode"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="6">
                <AppSelect
                  v-model="mnForm.group_type"
                  :items="groupTypeOptions"
                  item-value="value"
                  item-title="title"
                  label="군 유형(군배정)"
                  :rules="[requiredValidator]"
                  :no-data-text="'프로젝트에 등록된 군이 없습니다.'"
                />
              </VCol>
              <VCol cols="6">
                <AppTextField
                  v-model="mnForm.drug_name"
                  label="명칭"
                  placeholder="예: 시험약 16mg"
                  :rules="[requiredValidator]"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="6">
                <AppTextField
                  v-model="mnForm.lot_no"
                  label="Lot No."
                  placeholder="Lot No. 입력"
                />
              </VCol>
              <VCol cols="6">
                <AppDateTimePicker
                  v-model="mnForm.expiry_date"
                  label="사용기한"
                  :rules="[requiredValidator]"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="6">
                <AppTextField
                  v-model="mnForm.threshold"
                  label="한계재고량"
                  type="number"
                  :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                  placeholder="숫자 입력"
                  :disabled="mnForm.threshold_na"
                  @keydown="onDecimalInput"
                />
              </VCol>
              <VCol cols="6" class="d-flex align-end">
                <VCheckbox
                  v-model="mnForm.threshold_na"
                  label="N/A (설정하지 않음)"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="6">
                <AppTextField
                  v-model="mnForm.per_animal_usage"
                  label="1마리당 사용약물량"
                  type="number"
                  :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                  placeholder="숫자 입력"
                  :disabled="mnForm.per_animal_usage_na"
                  @keydown="onDecimalInput"
                />
              </VCol>
              <VCol cols="6" class="d-flex align-end">
                <VCheckbox
                  v-model="mnForm.per_animal_usage_na"
                  label="N/A (설정하지 않음)"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardActions class="justify-end pa-4">
          <VBtn color="primary" @click="saveMnForm">저장</VBtn>
          <VBtn variant="outlined" @click="isMnDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ═══════════════════════════════════════ -->
    <!-- 입고 등록 다이얼로그                         -->
    <!-- ═══════════════════════════════════════ -->
    <VDialog v-model="isStockInDialogOpen" max-width="450" persistent no-click-animation @click:outside="onStockInOutsideClick">
      <VCard title="입고 등록">
        <VCardText>
          <VForm ref="refStockInForm" @submit.prevent="saveStockIn">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  :model-value="stockInForm.management_label"
                  label="관리번호"
                  disabled
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="6">
                <AppDateTimePicker
                  v-model="stockInForm.stock_in_date"
                  label="입고일"
                  :rules="[requiredValidator]"
                />
              </VCol>
              <VCol cols="6">
                <AppTextField
                  v-model="stockInForm.quantity"
                  label="입고수량"
                  type="number"
                  :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                  :rules="[requiredValidator]"
                  @keydown="onDecimalInput"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="stockInForm.receiver"
                  label="수령자"
                  placeholder="이름 입력"
                  :rules="[requiredValidator]"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardActions class="justify-end pa-4">
          <VBtn color="primary" @click="saveStockIn">등록</VBtn>
          <VBtn variant="outlined" @click="isStockInDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ═══════════════════════════════════════ -->
    <!-- 입고 이력 다이얼로그                         -->
    <!-- ═══════════════════════════════════════ -->
    <VDialog v-model="isStockInHistoryOpen" max-width="550">
      <VCard :title="`입고 이력 — ${stockInHistoryLabel}`">
        <VCardText>
          <VDataTable
            :items="stockInHistoryItems"
            :headers="stockInHistoryHeaders"
            density="comfortable"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.stock_in_date="{ item }">
              {{ formatDate(item.stock_in_date) }}
            </template>
          </VDataTable>
        </VCardText>
        <VCardActions class="justify-end pa-4">
          <VBtn variant="outlined" @click="isStockInHistoryOpen = false">닫기</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ═══════════════════════════════════════ -->
    <!-- DRUG No. 등록 다이얼로그                    -->
    <!-- ═══════════════════════════════════════ -->
    <VDialog v-model="isDrugDialogOpen" max-width="650">
      <VCard title="DRUG No. 등록">
        <VCardText>
          <VRow class="mb-4">
            <VCol cols="6">
              <AppSelect
                v-model="drugRegForm.management_number_id"
                :items="mnFilterOptions"
                label="관리번호"
                placeholder="선택하세요"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol v-if="selectedMnDetail" cols="6">
              <div class="text-body-2 pa-3 bg-grey-lighten-4 rounded">
                <strong>상세내역</strong><br>
                유형(군배정): {{ selectedMnDetail.group_type }}<br>
                명칭: {{ selectedMnDetail.drug_name }}<br>
                Lot No.: {{ selectedMnDetail.lot_no ?? '-' }}<br>
                사용기한: {{ formatDate(selectedMnDetail.expiry_date) }}
              </div>
            </VCol>
          </VRow>

          <div class="mb-2 text-subtitle-2 font-weight-bold">DRUG No. 목록</div>

          <div v-for="(row, idx) in drugRegForm.items" :key="idx" class="d-flex gap-2 mb-2 align-center">
            <AppTextField
              v-model="row.drug_no"
              placeholder="예: APOQ-009"
              density="compact"
              style="flex: 2"
              @keydown="onDrugNoKeydown"
            />
            <AppTextField
              v-model="row.quantity"
              type="number"
              :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
              placeholder="수량"
              @keydown="onDecimalInput"
              density="compact"
              style="flex: 1; max-width: 100px"
            />
            <VBtn
              icon
              variant="text"
              color="error"
              size="x-small"
              :disabled="drugRegForm.items.length <= 1"
              @click="removeDrugRow(idx)"
            >
              <VIcon icon="tabler-trash" size="16" />
            </VBtn>
          </div>

          <VBtn variant="tonal" size="small" class="mt-2" @click="addDrugRow">
            + 추가
          </VBtn>

          <p class="text-caption text-medium-emphasis mt-3">
            ※ 관리번호 선택 시 군유형, 명칭, Lot No., 사용기한 정보가 자동으로 연동됩니다.
          </p>
        </VCardText>
        <VCardActions class="justify-end pa-4">
          <VBtn color="primary" @click="saveDrugs">저장</VBtn>
          <VBtn variant="outlined" @click="isDrugDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
    <!-- ═══════════════════════════════════════ -->
    <!-- DRUG No. 수정 다이얼로그                    -->
    <!-- ═══════════════════════════════════════ -->
    <VDialog v-model="isDrugEditDialogOpen" max-width="450">
      <VCard title="DRUG No. 수정">
        <VCardText>
          <VRow>
            <VCol cols="12">
              <AppTextField
                v-model="drugEditForm.drug_no"
                label="DRUG No."
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12">
              <AppSelect
                v-model="drugEditForm.management_number_id"
                :items="mnFilterOptions"
                label="관리번호"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="6">
              <AppTextField
                v-model="drugEditForm.quantity"
                label="수량"
                type="number"
                :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                @keydown="onDecimalInput"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="justify-end pa-4">
          <VBtn color="primary" @click="saveDrugEdit">저장</VBtn>
          <VBtn variant="outlined" @click="isDrugEditDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.rtms-table :deep(th),
.rtms-table :deep(td) {
  white-space: nowrap;
  font-size: 13px;
  padding: 6px 8px !important;
}

.rtms-table :deep(th) {
  font-size: 12px;
  font-weight: 600;
}

.rtms-table :deep(tr) {
  cursor: default;
}

.rtms-table :deep(td) {
  color: inherit;
  text-decoration: none;
}
</style>
